import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AbstractStateInterface } from '../../interface/abstract';
import { TriviaInterface } from '../../interface/trivia';
import { ApiService } from '../../utils/apiService';
import { InterfaceToSearchParams } from '../../utils/helper';

type DataState = AbstractStateInterface<TriviaInterface>;

const initialState: DataState = {
  data: [],
  detail: null,
  loading: false,
};

export interface TriviaSearch {
  id?: string;
}

export const createTrivia = createAsyncThunk('admin-api/trivia', async (payload: FormData, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/trivia`, 'POST', payload);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const getAllTrivias = createAsyncThunk('admin-api/trivia/all', async (_, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/trivia/all`, 'GET', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const findTriviaByCondition = createAsyncThunk(
  'admin-api/trivia/get',
  async (payload: TriviaSearch, thunkAPI) => {
    const searchParams = InterfaceToSearchParams(payload);
    try {
      const response = await ApiService(`admin-api/trivia?${searchParams}`, 'GET', null);
      if (response.statusCode !== 200) {
        return thunkAPI.rejectWithValue({
          code: response.statusCode,
          error: response.error || 'Login failed',
        });
      }
      return {
        code: response.statusCode,
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        code: error.response?.status || 500,
        error: error.message || 'Error',
      });
    }
  },
);

export const updateTrivia = createAsyncThunk('admin-api/trivia/put', async (payload: FormData, thunkAPI) => {
  try {
    const response = await ApiService(`admin-api/trivia`, 'PUT', payload);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

export const deleteTrivia = createAsyncThunk('admin-api/trivia/delete', async (payload: TriviaSearch, thunkAPI) => {
  const searchParams = InterfaceToSearchParams(payload);
  try {
    const response = await ApiService(`admin-api/trivia?${searchParams}`, 'DELETE', null);
    if (response.statusCode !== 200) {
      return thunkAPI.rejectWithValue({
        code: response.statusCode,
        error: response.error || 'Login failed',
      });
    }
    return {
      code: response.statusCode,
      data: response.data,
      message: response.message,
    };
  } catch (error: any) {
    return thunkAPI.rejectWithValue({
      code: error.response?.status || 500,
      error: error.message || 'Error',
    });
  }
});

const triviaSlice = createSlice({
  name: 'trivia',
  initialState,
  reducers: {
    resetTriviaSlice: () => {
      return { ...initialState };
    },
    resetDetail: (state) => {
      state.detail = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllTrivias.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllTrivias.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.data = action.payload.data as TriviaInterface[];
        } else {
          state.data = [];
        }
      })
      .addCase(getAllTrivias.rejected, (state) => {
        state.loading = false;
      })
      .addCase(findTriviaByCondition.pending, (state) => {
        state.loading = true;
      })
      .addCase(findTriviaByCondition.fulfilled, (state, action) => {
        state.loading = false;
        if (Array.isArray(action.payload.data)) {
          state.detail = action.payload.data[0] as TriviaInterface;
        } else {
          state.detail = null;
        }
      })
      .addCase(findTriviaByCondition.rejected, (state) => {
        state.loading = false;
      });
  },
});
export const { resetTriviaSlice, resetDetail } = triviaSlice.actions;
export default triviaSlice.reducer;
